import { useSearchParams } from 'react-router-dom';
import { CurrentOrPreviousGroup } from '../../../../../../api/groups';
import { useCallback, useEffect, useMemo } from 'react';
import { SchoolYearValue, useResultsDataContext } from '../../ResultsDataContext';
import { useFilterInLocalStorage } from '../../../components/per-student/hooks/useFilterInLocalStorage';
import { FilterSearchParam } from '../../../../../../shared-components/elementis/types/FilterSearchParam';

interface Opts {
  selectedSchoolYear: SchoolYearValue | null;
  selectedTab: 'STUDENT' | 'EXERCISE';
}

export function useSelectedSchool({ selectedSchoolYear, selectedTab }: Opts) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { groups, modules } = useResultsDataContext();
  const { getStoredData } = useFilterInLocalStorage(modules[0]?.id);

  const selectedSchoolId = searchParams.get(FilterSearchParam.School);

  const selectedSchool = useMemo(() => {
    if (selectedSchoolId) {
      return groups.find(x => x.school.id === selectedSchoolId)?.school || null;
    }

    return null;
  }, [groups, selectedSchoolId]);

  const setSelectedSchool = useCallback(
    (school: CurrentOrPreviousGroup['school']) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set(FilterSearchParam.School, school.id);

        const groupsToSelect = groups.filter(g => g.school.id === school.id && g.schoolYear === selectedSchoolYear?.id);

        if (groupsToSelect.length !== 0) {
          newParams.set(
            FilterSearchParam.Groups,
            selectedTab === 'STUDENT' ? groupsToSelect[0].id : groupsToSelect.map(g => g.id).join(','),
          );
        }

        return newParams;
      });
    },
    [groups, selectedSchoolYear, selectedTab, setSearchParams],
  );

  useEffect(() => {
    if (selectedSchoolId) return;

    const storedData = getStoredData();
    if (storedData === null) return;

    const school = groups.find(g => g.school.id === storedData.schoolId)?.school;
    if (!school) return;

    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set(FilterSearchParam.School, school.id);
      return newParams;
    });
  }, [getStoredData, groups, selectedSchoolId, setSearchParams]);

  return [selectedSchool, setSelectedSchool] as const;
}
